export enum DocumentRecordType {
  File = 'file',
  Folder = 'folder',
}

export enum AddressCheckStatus {
  Match = 'MATCH',
  NeedsConfirmation = 'NEEDS_CONFIRMATION',
  Overridden = 'OVERRIDDEN',
}

export enum UploadBatchTerminalStatus {
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
  NeedsAttention = 'NEEDS_ATTENTION',
  Incomplete = 'INCOMPLETE',
}

/**
 * Document classification types produced by the BE classifier lambda.
 *
 * Source of truth: keep aligned with the same enum on the BE
 * (packages/functions/common/models/DocumentRecord.ts) and with the
 * `VALID_TYPES` derivation in the classifier lambda. The classifier prompt
 * (admin /admin/logic Content tab) must list the same codes.
 *
 * PENDING is a frontend/upload-time placeholder, not produced by the
 * classifier — rendered with its own "Processing…" chip styling.
 */
export enum DocumentClassificationType {
  // INSPECTION REPORTS
  GHI = 'GHI',
  POOL = 'POOL',
  CHIMNEY = 'CHIMNEY',
  HVAC = 'HVAC',
  ELEC = 'ELEC',
  PLUMB = 'PLUMB',
  FOUND = 'FOUND',
  ENV = 'ENV',
  SHI = 'SHI',
  PEST = 'PEST',
  ROOF = 'ROOF',
  SEWER = 'SEWER',
  // REPAIR & COST DOCS
  ESTIMATE = 'ESTIMATE',
  INVOICE = 'INVOICE',
  // HAZARD DISCLOSURES
  NHD = 'NHD',
  // SELLER DISCLOSURES
  SELLER_DISCLOSURE = 'SELLER_DISCLOSURE',
  // HOA DISCLOSURES
  HOA_DEMAND = 'HOA_DEMAND',
  HOA_BOARD_MINUTES = 'HOA_BOARD_MINUTES',
  HOA_OPERATING_RULES = 'HOA_OPERATING_RULES',
  HOA_ANNUAL_BUDGET = 'HOA_ANNUAL_BUDGET',
  HOA_INSURANCE_SUMMARY = 'HOA_INSURANCE_SUMMARY',
  HOA_CCRS = 'HOA_CCRS',
  HOA_BYLAWS = 'HOA_BYLAWS',
  HOA_ARTICLES_OF_INCORPORATION = 'HOA_ARTICLES_OF_INCORPORATION',
  HOA_FINANCIAL_STATEMENT = 'HOA_FINANCIAL_STATEMENT',
  HOA_ENVIRONMENTAL_REPORT = 'HOA_ENVIRONMENTAL_REPORT',
  HOA_AGE_RESTRICTION = 'HOA_AGE_RESTRICTION',
  HOA_RENTAL_RESTRICTION = 'HOA_RENTAL_RESTRICTION',
  HOA_ASSESSMENT_CHANGE_NOTICE = 'HOA_ASSESSMENT_CHANGE_NOTICE',
  HOA_TRANSFER_DISCLOSURE_FORM = 'HOA_TRANSFER_DISCLOSURE_FORM',
  HOA_CLOSING_INFORMATION = 'HOA_CLOSING_INFORMATION',
  HOA_COMMON_AREA_DEFECT_LIST = 'HOA_COMMON_AREA_DEFECT_LIST',
  HOA_COMMON_AREA_DEFECT_SETTLEMENT = 'HOA_COMMON_AREA_DEFECT_SETTLEMENT',
  HOA_EEE_INSPECTION_REPORT = 'HOA_EEE_INSPECTION_REPORT',
  HOA_VIOLATION_NOTICE = 'HOA_VIOLATION_NOTICE',
  HOA_PACKAGE = 'HOA_PACKAGE',
  // FALLBACK
  OTHER = 'OTHER',
  // FE-only upload-time placeholder
  PENDING = 'PENDING',
}

export type DocumentTypeGroup =
  | 'inspection'
  | 'repair'
  | 'hazard'
  | 'seller-disclosure'
  | 'hoa'
  | 'other'
  | 'pending';

/** Group color palette — chips render the group color, not a per-type color. */
export const DOCUMENT_GROUP_COLORS: Record<DocumentTypeGroup, string> = {
  inspection: '#5AE9FF',
  repair: '#FF9A4D',
  hazard: '#DB5282',
  'seller-disclosure': '#bbb7af',
  hoa: '#8E7CFF',
  other: '#6b6b6b',
  pending: '#FFC107',
};

/** Group + label per type — single source of truth for chip rendering. */
export const DOCUMENT_TYPE_CONFIG: Record<
  DocumentClassificationType,
  { label: string; group: DocumentTypeGroup }
> = {
  [DocumentClassificationType.GHI]: { label: 'General', group: 'inspection' },
  [DocumentClassificationType.POOL]: { label: 'Pool', group: 'inspection' },
  [DocumentClassificationType.CHIMNEY]: { label: 'Chimney', group: 'inspection' },
  [DocumentClassificationType.HVAC]: { label: 'HVAC', group: 'inspection' },
  [DocumentClassificationType.ELEC]: { label: 'Electrical', group: 'inspection' },
  [DocumentClassificationType.PLUMB]: { label: 'Plumbing', group: 'inspection' },
  [DocumentClassificationType.FOUND]: { label: 'Foundation', group: 'inspection' },
  [DocumentClassificationType.ENV]: { label: 'Environmental', group: 'inspection' },
  [DocumentClassificationType.SHI]: { label: 'Specialist', group: 'inspection' },
  [DocumentClassificationType.PEST]: { label: 'Pest', group: 'inspection' },
  [DocumentClassificationType.ROOF]: { label: 'Roof', group: 'inspection' },
  [DocumentClassificationType.SEWER]: { label: 'Sewer', group: 'inspection' },
  [DocumentClassificationType.ESTIMATE]: { label: 'Estimate', group: 'repair' },
  [DocumentClassificationType.INVOICE]: { label: 'Invoice', group: 'repair' },
  [DocumentClassificationType.NHD]: { label: 'Hazard', group: 'hazard' },
  [DocumentClassificationType.SELLER_DISCLOSURE]: {
    label: 'Seller Disclosures',
    group: 'seller-disclosure',
  },
  [DocumentClassificationType.HOA_DEMAND]: { label: 'HOA Demand', group: 'hoa' },
  [DocumentClassificationType.HOA_BOARD_MINUTES]: { label: 'HOA Board Minutes', group: 'hoa' },
  [DocumentClassificationType.HOA_OPERATING_RULES]: { label: 'HOA Operating Rules', group: 'hoa' },
  [DocumentClassificationType.HOA_ANNUAL_BUDGET]: { label: 'HOA Annual Budget', group: 'hoa' },
  [DocumentClassificationType.HOA_INSURANCE_SUMMARY]: {
    label: 'HOA Insurance Summary',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_CCRS]: { label: 'HOA CC&Rs', group: 'hoa' },
  [DocumentClassificationType.HOA_BYLAWS]: { label: 'HOA Bylaws', group: 'hoa' },
  [DocumentClassificationType.HOA_ARTICLES_OF_INCORPORATION]: {
    label: 'HOA Articles of Incorporation',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_FINANCIAL_STATEMENT]: {
    label: 'HOA Financial Review',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_ENVIRONMENTAL_REPORT]: {
    label: 'HOA Environmental Report',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_AGE_RESTRICTION]: { label: 'HOA Age Restriction', group: 'hoa' },
  [DocumentClassificationType.HOA_RENTAL_RESTRICTION]: {
    label: 'HOA Rental Restriction',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_ASSESSMENT_CHANGE_NOTICE]: {
    label: 'HOA Assessment Change',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_TRANSFER_DISCLOSURE_FORM]: {
    label: 'HOA Transfer Disclosure Form',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_CLOSING_INFORMATION]: {
    label: 'HOA Closing Information',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_COMMON_AREA_DEFECT_LIST]: {
    label: 'HOA Defect List',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_COMMON_AREA_DEFECT_SETTLEMENT]: {
    label: 'HOA Defect Settlement',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_EEE_INSPECTION_REPORT]: {
    label: 'HOA Balcony / Deck Inspection',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_VIOLATION_NOTICE]: {
    label: 'HOA Violation Notice',
    group: 'hoa',
  },
  [DocumentClassificationType.HOA_PACKAGE]: { label: 'HOA Package', group: 'hoa' },
  [DocumentClassificationType.OTHER]: { label: 'Other', group: 'other' },
  [DocumentClassificationType.PENDING]: { label: 'Processing…', group: 'pending' },
};

/** Display order for document chips — General first, then by structural importance, rest alpha. */
export const DOCUMENT_TYPE_SORT_ORDER: DocumentClassificationType[] = [
  DocumentClassificationType.GHI,
  DocumentClassificationType.ROOF,
  DocumentClassificationType.FOUND,
  DocumentClassificationType.HVAC,
  DocumentClassificationType.ELEC,
  DocumentClassificationType.PLUMB,
  DocumentClassificationType.PEST,
  DocumentClassificationType.SEWER,
  DocumentClassificationType.CHIMNEY,
  DocumentClassificationType.POOL,
  DocumentClassificationType.ENV,
  DocumentClassificationType.SHI,
  DocumentClassificationType.ESTIMATE,
  DocumentClassificationType.INVOICE,
  DocumentClassificationType.NHD,
  DocumentClassificationType.SELLER_DISCLOSURE,
  DocumentClassificationType.HOA_DEMAND,
  DocumentClassificationType.HOA_BOARD_MINUTES,
  DocumentClassificationType.HOA_OPERATING_RULES,
  DocumentClassificationType.HOA_ANNUAL_BUDGET,
  DocumentClassificationType.HOA_INSURANCE_SUMMARY,
  DocumentClassificationType.HOA_CCRS,
  DocumentClassificationType.HOA_BYLAWS,
  DocumentClassificationType.HOA_ARTICLES_OF_INCORPORATION,
  DocumentClassificationType.HOA_FINANCIAL_STATEMENT,
  DocumentClassificationType.HOA_ENVIRONMENTAL_REPORT,
  DocumentClassificationType.HOA_AGE_RESTRICTION,
  DocumentClassificationType.HOA_RENTAL_RESTRICTION,
  DocumentClassificationType.HOA_ASSESSMENT_CHANGE_NOTICE,
  DocumentClassificationType.HOA_TRANSFER_DISCLOSURE_FORM,
  DocumentClassificationType.HOA_CLOSING_INFORMATION,
  DocumentClassificationType.HOA_COMMON_AREA_DEFECT_LIST,
  DocumentClassificationType.HOA_COMMON_AREA_DEFECT_SETTLEMENT,
  DocumentClassificationType.HOA_EEE_INSPECTION_REPORT,
  DocumentClassificationType.HOA_VIOLATION_NOTICE,
  DocumentClassificationType.HOA_PACKAGE,
  DocumentClassificationType.OTHER,
];

/** Human-readable labels — derived from DOCUMENT_TYPE_CONFIG. */
export const DOCUMENT_TYPE_LABELS: Record<DocumentClassificationType, string> = Object.fromEntries(
  Object.entries(DOCUMENT_TYPE_CONFIG).map(([k, v]) => [k, v.label]),
) as Record<DocumentClassificationType, string>;

/** Per-type colors resolved through the group — chips inherit group color. */
export const DOCUMENT_TYPE_COLORS: Record<DocumentClassificationType, string> = Object.fromEntries(
  Object.entries(DOCUMENT_TYPE_CONFIG).map(([k, v]) => [k, DOCUMENT_GROUP_COLORS[v.group]]),
) as Record<DocumentClassificationType, string>;

export interface DocumentRecord {
  id: string;
  parentId?: string;
  documentId?: string;
  type: DocumentRecordType;
  name: string;
  userId: string;
  propertyId: string;
  /** Original uploader before any ownership transfer — preserved so admins
   *  can see who first uploaded a document after it's been reassigned to a
   *  new property owner. */
  uploadedByUserId?: string;
  order?: number;
  /** AI-classified document type. */
  documentType?: DocumentClassificationType;
  /** Confidence score (0-1) for the classification. */
  documentTypeConfidence?: number;
  /** Address verification result against the property the doc was uploaded to. */
  addressCheckStatus?: AddressCheckStatus;
  /** LLM-extracted subject address from the document. */
  extractedAddress?: string;
  /** Model's self-reported confidence in the extraction. */
  extractedAddressConfidence?: number;
  /** Backend-defined confidence threshold the classifier compared against —
   *  surfaced on NEEDS_CONFIRMATION rows so the dialog renders the live value
   *  rather than a frontend constant that can drift. */
  confidenceThreshold?: number;
  /** Set once the AI pipeline completes and the InspectionReport is created. */
  inspectionReportId?: string;
  /** Page count of the uploaded PDF, computed during ingestion. Absent on
   *  non-PDF uploads and on documents uploaded before this field existed. */
  pageCount?: number;
  /** Logical forms and source-page lineage detected inside a physical seller-disclosure PDF. */
  sellerDisclosureManifest?: {
    artifactVersion: 1;
    registryVersion: string;
    mode: 'compiled-packet' | 'individual-form';
    extractionId: string;
    splitId?: string;
    schemaId: string;
    artifactS3Key: string;
    pageCount: number;
    forms: Array<{
      formKey: string;
      shortLabel: string;
      title: string;
      assignedPages: number[];
      primaryPage: number;
      needsReview: boolean;
      downstreamRoute?: 'nhd';
    }>;
    manifestIntegrity: {
      totalPages: number;
      assignedPageCount: number;
      unassignedPages: number[];
      multiplyAssignedPages: Array<{ page: number; formKeys: string[] }>;
      complete: boolean;
    };
    processedAt: number;
  };
  sellerDisclosureProcessedAt?: number;
  hoaPackageManifest?: {
    artifactVersion: 1;
    registryVersion: string;
    extractionId: string;
    splitId: string;
    artifactS3Key: string;
    pageCount: number;
    documents: Array<{
      manifestItemId: string;
      classification: string;
      title: string;
      assignedPages: number[];
      primaryPage: number;
      checklistItemIds: string[];
      confidence: 'high' | 'medium' | 'low';
      issues?: Array<{
        code: string;
        severity: 'blocking' | 'review' | 'informational';
        manifestItemId?: string;
        pages?: number[];
        message?: string;
      }>;
      needsReview: boolean;
      schemaStatus: 'pending' | 'succeeded' | 'failed' | 'not-applicable';
    }>;
    manifestIntegrity: {
      totalPages: number;
      assignedPageCount: number;
      unassignedPages: number[];
      multiplyAssignedPages: Array<{ page: number; manifestItemIds: string[] }>;
      complete: boolean;
    };
    issues?: Array<{
      code: string;
      severity: 'blocking' | 'review' | 'informational';
      manifestItemId?: string;
      pages?: number[];
      message?: string;
    }>;
    processingStatus?: 'processing' | 'complete' | 'partial';
    processedAt: number;
  };
  /** Set when an HOA-specific extraction route finishes. */
  hoaProcessedAt?: number;
  uploadBatchTerminalStatus?: UploadBatchTerminalStatus;
  createdAt: number;
  updatedAt: number;
}

export interface CreateDocumentRecord {
  parentId: string;
  documentId: string;
  type: DocumentRecordType;
  name: string;
}

export interface UpdateDocumentRecord {
  parentId?: string;
  name?: string;
}
